<script setup>
import { computed, ref } from 'vue'
import OfferingCard from './OfferingCard.vue'

/**
 * What the applicant will offer, in the two products the site sells.
 *
 * This replaces a grid of four teaching methods — study material, recorded
 * video, group tutoring, 1:1 — which asked an applicant to assemble a product
 * out of parts and let them tick "Group Tutoring" on its own, which is not
 * something the site sells. The parts are still what gets stored; see
 * App\Data\TutorOffering.
 */
const props = defineProps({
  offerings: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'What You Offer'
  },
  description: {
    type: String,
    default: 'Choose the products you want to teach on Bindle'
  },
  required: {
    type: Boolean,
    default: true
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue'])

const localErrors = ref({})

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

const isSelected = (key) => props.modelValue.includes(key)

const toggleOffering = (key) => {
  const selected = [...props.modelValue]
  const index = selected.indexOf(key)

  if (index === -1) {
    selected.push(key)
  } else {
    selected.splice(index, 1)
  }

  emit('update:modelValue', selected)

  if (localErrors.value.offerings) {
    delete localErrors.value.offerings
  }
}

const validate = () => {
  const errors = {}

  if (props.required && props.modelValue.length === 0) {
    errors.offerings = 'Please select at least one thing you want to offer'
  }

  localErrors.value = errors

  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div class="md:bg-white md:rounded-2xl md:shadow-lg p-0 md:p-10">
    <div class="mb-8">
      <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">
        {{ title }} <span v-if="required" class="text-red-500">*</span>
      </h2>
      <p class="text-gray-600">{{ description }}</p>

      <!-- Error Message -->
      <div
        v-if="displayErrors.offerings || displayErrors.teaching_method_ids"
        class="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl"
      >
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-red-600 text-xl mt-0.5">error</span>
          <div>
            <p class="text-red-600 font-semibold text-base">
              {{ displayErrors.offerings || displayErrors.teaching_method_ids }}
            </p>
            <p class="text-red-700 text-sm mt-1">Choose at least one of the options below.</p>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <OfferingCard
        v-for="offering in offerings"
        :key="offering.key"
        :offering="offering"
        :selected="isSelected(offering.key)"
        @toggle="toggleOffering"
      />
    </div>
  </div>
</template>
